import { parseYamlTasks } from '@/backend/helpers/parseYamlTasks';
import { EquipmentItem } from '@/backend/models/equipmentItem';
import { useEquipmentStore } from '@/frontend/store/EquipmentStore';
import { useInstrumentStore } from '@/frontend/store/InstrumentStore';
import { needsEngineConnection } from '@/types/instrumentBridgeTypes';

/**
 * The equipment a commanding needs connected before it can run.
 *
 * Ported from veriform-desktop's `useGetRequiredItemsForAutomation`
 * (Commandings/useRequiredItemsForAutomation.ts). The shape is narrower: that
 * one also reports instruments addressed by name, which it can because the
 * procedure and the instruments live in the same database. Here a task
 * addresses an equipment identifier from this procedure, and whether anything
 * is behind it is a question for the desktop app.
 *
 * `veriform` tasks need no equipment at all, and a `resourcery` task brings its
 * own, which is why desktop exempts both. Equipment with no instrument assigned
 * is not required either, as there: desktop only asks of an assigned instrument
 * whether it is running.
 */

const VERIFORM = 'veriform';
const RESOURCERY = 'resourcery';

export function useRequiredConnections(yaml: string | undefined): EquipmentItem[] {
  const equipmentList = useEquipmentStore(state => state.equipmentList);
  // Read as values rather than through the store's `isReady`, so this
  // recomputes when a connection starts or an assignment changes.
  const instruments = useInstrumentStore(state => state.instruments);
  const connectedEquipmentIds = useInstrumentStore(state => state.connectedEquipmentIds);
  const instrumentIdByEquipmentId = useInstrumentStore(state => state.instrumentIdByEquipmentId);

  if (!yaml?.trim()) return [];

  const addressed = new Set(
    parseYamlTasks(yaml)
      .map(task => task.instrumentName)
      .filter(name => name && name !== VERIFORM),
  );

  if (addressed.has(RESOURCERY)) return [];

  const notReady = (equipmentId: string) => {
    const assigned = instruments.find(({ id }) => id === instrumentIdByEquipmentId[equipmentId]);
    if (!assigned || !needsEngineConnection(assigned)) return false;
    return !connectedEquipmentIds.includes(equipmentId);
  };

  return equipmentList.filter(
    equipment => addressed.has(equipment.identifier) && notReady(equipment.id),
  );
}
